import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useToast } from 'primevue/usetoast';
import { z } from 'zod';
import type {
  ContactUsForm,
  ContactUsPayload,
} from '~/types/auth/forms/ContactUsForm';
import type { ApiInstance } from '~/types/plugins/api';
import ContactUsFormContent from '~/utils/contents/auth/forms/ContactUsForm';
import { $parseErrorMessage } from '~/utils/helpers';

export default function useContactUs() {
  const loading = ref(false);
  const form = ref<ContactUsForm>(getInitialState());

  const toast = useToast();

  const validations = z.object({
    email: z
      .string()
      .min(1, {
        message: `${ContactUsFormContent.fields.email.label} is required.`,
      })
      .email({ message: 'Please enter a valid email address.' }),
    first_name: z.string().min(1, {
      message: `${ContactUsFormContent.fields.first_name.label} is required.`,
    }),
    last_name: z.string().min(1, {
      message: `${ContactUsFormContent.fields.last_name.label} is required.`,
    }),
    company_name: z.string().optional().nullable(),
    template_experience: z.array(z.string()).optional(),
    monthly_doc_volume: z
      .string()
      .optional()
      .nullable()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: 'Please enter a valid number.',
      })
      .refine(
        (val) =>
          !val ||
          Number(val) <= ContactUsFormContent.fields.monthly_doc_volume.max,
        {
          message: `Value must not exceed ${ContactUsFormContent.fields.monthly_doc_volume.max.toLocaleString()}.`,
        },
      ),
    message: z.string().optional().nullable(),
  });

  const resolver = zodResolver(validations);

  const submit = async (): Promise<boolean> => {
    if (!validations.safeParse(form.value).success) {
      return false;
    }

    loading.value = true;

    try {
      const { $apiClient } = useNuxtApp() as unknown as {
        $apiClient: ApiInstance;
      };

      const payload: ContactUsPayload = {
        email: form.value.email,
        first_name: form.value.first_name,
        last_name: form.value.last_name,
        company_name: form.value.company_name || '',
        worked_with_word: form.value.template_experience.includes('word'),
        worked_with_excel: form.value.template_experience.includes('excel'),
        document_volume: Number(form.value.monthly_doc_volume) || 0,
        message: form.value.message || '',
      };

      await $apiClient.post('waitlist', payload);
      reset();
      return true;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: $parseErrorMessage(error),
        life: 3000,
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const reset = (): void => {
    form.value = getInitialState();
  };

  function getInitialState(): ContactUsForm {
    return {
      email: '',
      first_name: '',
      last_name: '',
      company_name: '',
      template_experience: [],
      monthly_doc_volume: '',
      message: '',
    };
  }

  return {
    loading,
    form,
    validations,
    resolver,
    submit,
    reset,
  };
}
